import * as React from "react";
import { toast } from "sonner";
import { X, Plus, Zap, Trash2, Play, Pause } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * AutomationsPanel — RIGHT-side slide-over showing per-board automations.
 *
 * Backend catalog:
 *   Triggers: card_moved_to_list (config.list_id) | card_due_date_passed | all_checklist_items_done
 *   Actions:  move_to_list (config.list_id) | assign_member (config.user_id)
 *             add_label (config.label_id)   | mark_done
 *
 * canManage gates all mutations; without it, this is read-only.
 */
const TRIGGER_LABELS = {
  card_moved_to_list: "Card moved to list",
  card_due_date_passed: "Card is overdue",
  all_checklist_items_done: "Checklist completed",
};
const ACTION_LABELS = {
  move_to_list: "Move to list",
  assign_member: "Assign member",
  add_label: "Add label",
  mark_done: "Mark done",
};

export default function AutomationsPanel({ onClose, companyId, boardId, lists, labels, canManage }) {
  const [rules, setRules] = React.useState([]);
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [members, setMembers] = React.useState([]);
  const [creating, setCreating] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: r }] = await Promise.all([
        api.get(`/companies/${companyId}/boards/${boardId}/automations`),
        api.get(`/companies/${companyId}/boards/${boardId}/automations/runs`, { params: { limit: 20 } }),
      ]);
      setRules(a.automations || []);
      setRuns(r.runs || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load automations");
    } finally {
      setLoading(false);
    }
  }, [companyId, boardId]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    api.get(`/companies/${companyId}/search/members`, { params: { q: "", limit: 100 } })
      .then(({ data }) => setMembers(data.members || []))
      .catch(() => setMembers([]));
  }, [companyId]);

  // Live sync
  React.useEffect(() => {
    const on = (e) => {
      const evt = e.detail;
      if (evt?.module !== "boards") return;
      if (evt.payload?.board_id !== boardId) return;
      if (["automation.created", "automation.updated", "automation.deleted"].includes(evt.type)) {
        load();
      }
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [boardId, load]);

  async function toggleEnabled(rule) {
    try {
      await api.patch(`/companies/${companyId}/boards/${boardId}/automations/${rule.id}`, {
        enabled: !rule.enabled,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not toggle");
    }
  }

  async function del(rule) {
    if (!window.confirm(`Delete automation "${rule.name}"?`)) return;
    try {
      await api.delete(`/companies/${companyId}/boards/${boardId}/automations/${rule.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete");
    }
  }

  function ruleSummary(rule) {
    const t = rule.trigger || {};
    const a = rule.action || {};
    const parts = [];
    if (t.type === "card_moved_to_list") {
      const l = lists.find((x) => x.id === t.config?.list_id);
      parts.push(`When a card enters ${l ? `"${l.name}"` : "(list missing)"}`);
    } else if (t.type === "card_due_date_passed") {
      parts.push("When a card becomes overdue");
    } else {
      parts.push("When a card's checklist is completed");
    }
    if (a.type === "move_to_list") {
      const l = lists.find((x) => x.id === a.config?.list_id);
      parts.push(`move it to ${l ? `"${l.name}"` : "(list missing)"}`);
    } else if (a.type === "assign_member") {
      const m = members.find((x) => x.id === a.config?.user_id);
      parts.push(`assign ${m ? m.name : "(member missing)"}`);
    } else if (a.type === "add_label") {
      const l = labels.find((x) => x.id === a.config?.label_id);
      parts.push(`add label ${l ? `"${l.name}"` : "(label missing)"}`);
    } else {
      parts.push("mark it done");
    }
    return parts.join(", then ");
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-end"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="board-automations-panel"
    >
      <div className="absolute inset-0 bg-black/30" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[min(560px,92vw)] bg-card h-full shadow-card flex flex-col"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-brand-rule">
          <div>
            <div className="micro-label">Board</div>
            <div className="mt-0.5 text-[16px] font-semibold text-brand-ink flex items-center gap-2">
              <Zap size={14} className="text-brand-violet" /> Automations
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost h-8 w-8 !p-0"
                  data-testid="board-automations-close" aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
          <section>
            <div className="flex items-center justify-between">
              <div className="micro-label">Rules</div>
              {canManage && !creating && (
                <button type="button" onClick={() => setCreating(true)} className="btn-outline h-8"
                        data-testid="board-automations-new">
                  <Plus size={12} /> New rule
                </button>
              )}
            </div>

            {creating && (
              <RuleForm
                lists={lists}
                labels={labels}
                members={members}
                onCancel={() => setCreating(false)}
                onSubmit={async (payload) => {
                  try {
                    await api.post(`/companies/${companyId}/boards/${boardId}/automations`, payload);
                    setCreating(false);
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create rule");
                  }
                }}
              />
            )}

            <div className="mt-3 space-y-2">
              {loading && <div className="text-[12px] text-brand-muted">Loading…</div>}
              {!loading && rules.length === 0 && !creating && (
                <div className="text-[12px] text-brand-muted" data-testid="board-automations-empty">
                  No automations yet.
                </div>
              )}
              {rules.map((rule) => (
                <div key={rule.id} className="card-panel" data-testid={`board-automation-${rule.id}`}>
                  <div className="card-panel-body">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-brand-ink truncate">{rule.name}</div>
                        <div className="text-[11.5px] text-brand-muted">{ruleSummary(rule)}</div>
                        <div className="mt-1 flex items-center gap-1 text-[10.5px]">
                          <span className="chip">{TRIGGER_LABELS[rule.trigger?.type] || rule.trigger?.type}</span>
                          <span className="text-brand-muted">→</span>
                          <span className="chip chip-violet">{ACTION_LABELS[rule.action?.type] || rule.action?.type}</span>
                        </div>
                      </div>
                      {canManage && (
                        <>
                          <button type="button" onClick={() => toggleEnabled(rule)}
                                  className="btn-ghost h-8 w-8 !p-0"
                                  title={rule.enabled ? "Pause" : "Enable"}
                                  data-testid={`board-automation-toggle-${rule.id}`}>
                            {rule.enabled ? <Pause size={13} /> : <Play size={13} />}
                          </button>
                          <button type="button" onClick={() => del(rule)}
                                  className="btn-ghost h-8 w-8 !p-0 text-brand-coral"
                                  title="Delete"
                                  data-testid={`board-automation-delete-${rule.id}`}>
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="micro-label">Recent runs</div>
            <div className="mt-2 space-y-1">
              {runs.length === 0 && (
                <div className="text-[12px] text-brand-muted">No runs yet.</div>
              )}
              {runs.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-[11.5px] text-brand-ink"
                     data-testid={`board-automation-run-${r.id}`}>
                  <span className={`chip text-[9.5px] mono-num ${
                    r.result === "success" ? "chip-emerald" :
                    r.result === "error" ? "chip-coral" : "chip-amber"
                  }`}>
                    {r.result}
                  </span>
                  <span className="truncate flex-1">{r.automation_name}</span>
                  <span className="mono-num text-[10px] text-brand-muted">
                    {r.detail}
                  </span>
                  <span className="mono-num text-[10px] text-brand-faint">
                    {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function RuleForm({ lists, labels, members, onCancel, onSubmit }) {
  const [name, setName] = React.useState("");
  const [triggerType, setTriggerType] = React.useState("card_moved_to_list");
  const [triggerListId, setTriggerListId] = React.useState(lists[0]?.id || "");
  const [actionType, setActionType] = React.useState("mark_done");
  const [actionListId, setActionListId] = React.useState(lists[0]?.id || "");
  const [actionUserId, setActionUserId] = React.useState(members[0]?.id || "");
  const [actionLabelId, setActionLabelId] = React.useState(labels[0]?.id || "");
  const [busy, setBusy] = React.useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    const trigger = { type: triggerType, config: {} };
    if (triggerType === "card_moved_to_list") trigger.config.list_id = triggerListId;

    const action = { type: actionType, config: {} };
    if (actionType === "move_to_list") action.config.list_id = actionListId;
    else if (actionType === "assign_member") action.config.user_id = actionUserId;
    else if (actionType === "add_label") action.config.label_id = actionLabelId;
    try {
      await onSubmit({ name: name.trim(), trigger, action, enabled: true });
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="mt-3 card-panel" data-testid="board-automations-form">
      <div className="card-panel-body space-y-3">
        <div>
          <label className="micro-label">Rule name</label>
          <input className="field-input mt-1" value={name} onChange={(e) => setName(e.target.value)}
                 maxLength={120} required data-testid="board-automations-form-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="micro-label">When</label>
            <select className="field-input mt-1" value={triggerType}
                    onChange={(e) => setTriggerType(e.target.value)}
                    data-testid="board-automations-form-trigger-type">
              <option value="card_moved_to_list">Card moved to list</option>
              <option value="card_due_date_passed">Card is overdue</option>
              <option value="all_checklist_items_done">Checklist completed</option>
            </select>
          </div>
          {triggerType === "card_moved_to_list" && (
            <div>
              <label className="micro-label">Target list</label>
              <select className="field-input mt-1" value={triggerListId}
                      onChange={(e) => setTriggerListId(e.target.value)}
                      data-testid="board-automations-form-trigger-list">
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="micro-label">Then</label>
            <select className="field-input mt-1" value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    data-testid="board-automations-form-action-type">
              <option value="mark_done">Mark done</option>
              <option value="move_to_list">Move to list</option>
              <option value="assign_member">Assign member</option>
              <option value="add_label">Add label</option>
            </select>
          </div>
          {actionType === "move_to_list" && (
            <div>
              <label className="micro-label">List</label>
              <select className="field-input mt-1" value={actionListId}
                      onChange={(e) => setActionListId(e.target.value)}
                      data-testid="board-automations-form-action-list">
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          {actionType === "assign_member" && (
            <div>
              <label className="micro-label">Member</label>
              <select className="field-input mt-1" value={actionUserId}
                      onChange={(e) => setActionUserId(e.target.value)}
                      data-testid="board-automations-form-action-member">
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
          {actionType === "add_label" && (
            <div>
              <label className="micro-label">Label</label>
              <select className="field-input mt-1" value={actionLabelId}
                      onChange={(e) => setActionLabelId(e.target.value)}
                      data-testid="board-automations-form-action-label">
                {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                {labels.length === 0 && <option value="">(No labels on this board)</option>}
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={busy || !name.trim()}
                  className="btn-violet" data-testid="board-automations-form-submit">
            {busy ? "…" : "Create rule"}
          </button>
          <button type="button" onClick={onCancel} className="btn-outline"
                  data-testid="board-automations-form-cancel">
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}
